import { shipPayment, type StationState } from "@station-keeping/sim";

export type ModuleEvent =
  | { kind: "failure"; moduleId: string }
  | { kind: "escalation"; moduleId: string; stage: 2 | 3 }
  | { kind: "repaired"; moduleId: string }
  | { kind: "patched"; moduleId: string }
  | { kind: "upgraded"; moduleId: string; level: number }
  | { kind: "built"; moduleId: string };

export interface Payment {
  /** Poste d'amarrage que le vaisseau quitte. */
  berth: string;
  amount: number;
}

export interface StepEvents {
  modules: ModuleEvent[];
  payments: Payment[];
  /** Niveau atteint par la station pendant ces pas, s'il a changé. */
  levelUp: number | null;
}

const JOB_EVENTS = { repair: "repaired", patch: "patched" } as const;

/** Ce qui a changé entre deux états : pannes, escalades, fins de chantier et d'intervention, paiements, niveau. */
export function diffEvents(previous: StationState, current: StationState): StepEvents {
  const events: StepEvents = { modules: [], payments: [], levelUp: current.level > previous.level ? current.level : null };
  const before = new Map(previous.modules.map((module) => [module.id, module]));
  for (const module of current.modules) {
    const old = before.get(module.id);
    if (!old) continue;
    if (!old.failure && module.failure) events.modules.push({ kind: "failure", moduleId: module.id });
    else if (old.failure && module.failure && module.failure.stage > old.failure.stage) {
      events.modules.push({ kind: "escalation", moduleId: module.id, stage: module.failure.stage as 2 | 3 });
    }
    if (old.job && !module.job) {
      if (old.job.kind === "upgrade") events.modules.push({ kind: "upgraded", moduleId: module.id, level: module.level });
      else events.modules.push({ kind: JOB_EVENTS[old.job.kind], moduleId: module.id });
    }
    if (old.buildLeft > 0 && module.buildLeft === 0) events.modules.push({ kind: "built", moduleId: module.id });
  }

  // Un vaisseau qui vient de repartir a payé : on le compte comme au départ, à son poste et à la réputation d'avant.
  const shipsBefore = new Map(previous.ships.map((ship) => [ship.id, ship]));
  for (const ship of current.ships) {
    const old = shipsBefore.get(ship.id);
    if (!old || old.phase !== "docked" || ship.phase !== "leaving" || !old.berth) continue;
    const amount = shipPayment({ ...current, reputation: previous.reputation }, { ...ship, berth: old.berth });
    if (amount > 0) events.payments.push({ berth: old.berth, amount });
  }
  return events;
}
